<script>
import RepCounter from '@components/counters/rep-counter.vue';
import SetDetailsCircuit from '@components/circuit/set-details-circuit.vue';
import SetDetailsProgramCircuit from '@components/circuit/set-details-program-circuit.vue';
import {
  exerciseComputed,
  exerciseMethods,
  circuitComputed,
  circuitMethods,
  workoutComputed,
  workoutMethods,
  adminPanelComputed,
  clientProgramComputed,
} from '@state/helpers';

export default {
  components: {
    RepCounter,
    SetDetailsCircuit,
    SetDetailsProgramCircuit,
  },
  props: {
    circuit: {
      type: Object,
      required: true,
      default: () => {},
    },
    circuitIndex: {
      type: Number,
      required: true,
      default: null,
    },
    selectedCircuitIndex: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      hideDetails: true,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...exerciseComputed,
    ...circuitComputed,
    ...workoutComputed,
    ...clientProgramComputed,
    isSelected() {
      return (
        this.selectedCircuit &&
        this.selectedCircuit._id === this.circuit._id &&
        this.selectedCircuitIndex === this.circuitIndex
      );
    },
    hasExercises() {
      return (
        this.circuit && this.circuit.exercises && this.circuit.exercises.length
      );
    },
    isTrainerPreview() {
      return (
        this.panelContent &&
        this.panelContent.workoutPreviewType &&
        this.panelContent.workoutPreviewType === 'trainer'
      );
    },
    isStartedWorkout() {
      return (
        this.programDay &&
        this.programDay.workouts &&
        this.programDay.workouts.length > 0 &&
        this.programDay.workouts[0].clientInput &&
        this.programDay.workouts[0].clientInput.startedAt
      );
    },
    exercisesLength() {
      return this.circuit.exercises.length;
    },
  },
  methods: {
    ...exerciseMethods,
    ...circuitMethods,
    ...workoutMethods,
    toggleSelectCircuit(event) {
      if (event.target.classList.contains('card-circuit-header')) {
        this.isSelected
          ? this.setSelectedCircuit(null)
          : this.setSelectedCircuit(this.circuit);
      }
      this.$emit('on-select-circuit', this.circuitIndex);
    },
    onAdjustReps(circuitRep) {
      this.setUpdateWorkoutCircuitReps({
        circuitIndex: this.circuitIndex,
        count: circuitRep,
      });
    },
    showExerciseList() {
      this.$emit('show-exercise-list', this.circuitIndex);
    },
    selectExercise() {
      this.setSelectedExercise({});
    },
  },
  watch: {
    isAdminPanelActive() {
      // Close circuit and reset circuit intro input
      this.hideDetails = true;
    },
    exercisesLength(newVal, oldVal) {
      this.setAddUniqueId();
    },
  },
};
</script>

<template>
  <div class="card-circuit-container">
    <div
      :class="[
        'card-circuit-header',
        { selected: !isTrainerPreview && isSelected },
        { hide: isTrainerPreview && !hideDetails && !isStartedWorkout },
      ]"
      @click="toggleSelectCircuit($event)"
    >
      <h2 v-if="!isTrainerPreview">{{ circuit.label }}</h2>
      <h2 v-if="isTrainerPreview">{{
        `${circuit.label}   x ${circuit.circuitReps}`
      }}</h2>

      <div v-if="!isTrainerPreview" class="col-2 offset-3 rep-column">
        <RepCounter :count="circuit.circuitReps" @adjust-reps="onAdjustReps" />
      </div>
      <BaseButton
        v-if="isTrainerPreview"
        type="button"
        class="btn-primary btn-ghost btn-round"
        @click="hideDetails = !hideDetails"
      >
        <span>{{ hideDetails ? 'Show Details' : 'Hide Details' }}</span>
      </BaseButton>
    </div>
    <transition-group
      tag="div"
      name="swap-cards"
      class="card-circuit-content"
      v-if="hasExercises && !isTrainerPreview"
    >
      <SetDetailsCircuit
        v-for="(exercise, i) in circuit.exercises"
        :key="exercise.uniqueKey"
        :exercise="exercise"
        :circuitIndex="circuitIndex"
        :exerciseIndex="i"
        @show-exercise-list="showExerciseList"
      />
    </transition-group>
    <transition-group
      tag="div"
      name="swap-cards"
      v-if="isTrainerPreview"
      class="card-circuit-content"
      :style="{ minHeight: '1rem' }"
    >
      <SetDetailsProgramCircuit
        v-for="(exercise, i) in circuit.exercises"
        :key="exercise.uniqueKey"
        :exercise="exercise"
        :circuitIndex="circuitIndex"
        :circuit="circuit"
        :exerciseIndex="i"
        :hideDetails="hideDetails"
      />
    </transition-group>

    <div
      class="card-circuit-content empty"
      v-if="!hasExercises && !isTrainerPreview"
    >
      <div class="add-new-exercise-wrapper">
        <p
          >No exercises yet. <br />Add your first exercise to
          {{ circuit.label }}</p
        >
        <BaseButton
          type="button"
          class="btn-primary btn-round btn-ghost"
          @click="showExerciseList"
        >
          <img src="@assets/images/icon-curl.svg" /> Add First Exercise
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.card-circuit-container {
  background-color: white;
  border-radius: $base-border-radius-lg;
  box-shadow: 0 0.2rem 0.4rem rgba($color-black-wo-rgb, 0.08);
  margin-bottom: 1.6rem;
  transition: transform 300ms;
  transition-timing-function: ease-out;

  &:hover {
    box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.1);
    transform: scale(1.005);
  }

  .card-circuit-header {
    align-items: center;
    border-bottom: 6px solid $color-card-circuit-header-border;
    display: flex;
    justify-content: space-between;
    min-height: 9rem;
    padding: 1.5rem 2.5rem;

    &.hide {
      border-bottom: 0 solid $color-card-circuit-header-border;
    }

    &:hover {
      background-color: $color-card-circuit-header-hover;
      border-top-left-radius: $base-border-radius-lg;
      border-top-right-radius: $base-border-radius-lg;
      cursor: pointer;
    }

    &.selected {
      background-color: $color-card-circuit-header-bg-selected;
      border-color: $color-action;
      border-top-left-radius: $base-border-radius-lg;
      border-top-right-radius: $base-border-radius-lg;
    }

    h2 {
      margin-bottom: 0;
      margin-right: 3rem;
    }
  }

  .card-circuit-content {
    min-height: 22.6rem;

    &.empty {
      align-items: center;
      display: flex;
      justify-content: center;
    }
  }

  .swap-cards-move {
    transition: transform 300ms;
  }

  .add-new-exercise-wrapper {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    width: 25%;
  }

  .btn-primary {
    span {
      white-space: nowrap;
    }
  }
}
</style>
